<template>
  <div class="enablebanking-consent tilisy-consent">
    <div v-if="error" class="tilisy-error">
      <p class="text-justify">{{ error }}</p>
    </div>
    <div v-if="!error && !starting" class="enablebanking-consent-summary tilisy-terms">
      <p class="text-justify">
        <i18n-t tag="span" keypath="Authentication is initiated by {0}."><strong>{{ appName }}</strong></i18n-t>
        <span>&nbsp;</span>
        <i18n-t tag="span" keypath="After you complete authentication, {0} will be shared with the authentication initiator." class="mr-1"><em>{{ $t(accessScope) }}</em></i18n-t>
        <span>&nbsp;</span>
        <i18n-t tag="span" keypath="By pressing {0} button you agree with the {1}."><span>"{{ externalConfirm || $t('Continue authentication') }}"</span><a :href="termsUrl" target="_blank">{{ isEnablebankingOrigin ? $t('terms of Enable Banking API service') : $t('terms of this service') }}</a></i18n-t>
        <i18n-t tag="span" keypath="Press {0} button if you are not willing to share your payment accounts data." v-if="canCancel"><span>"{{ $t('Cancel') }}"</span></i18n-t>
      </p>
    </div>
    <div v-if="!error && !starting && (!externalConfirm || canCancel)" class="tilisy-confirmation">
      <div class="form-inline justify-content-center" v-if="!externalConfirm">
        <button class="btn btn-theme" v-on:click="submitConsent" :disabled="confirming || confirmed">{{ $t('Continue authentication') }}</button>
      </div>
      <div class="text-center" v-if="canCancel">
        <button class="btn btn-link" v-on:click="cancelAuth" :disabled="confirming || confirmed">{{ $t('Cancel') }}</button>
      </div>
    </div>
    <div v-if="!error && !starting" class="enablebanking-consent-about tilisy-about">
      <p><strong>{{ $t('About {appName}', { appName: appName }) }}</strong></p>
      <p class="text-justify">{{ appDescriptionLocal }}</p>
      <about-service v-if="isEnablebankingOrigin" />
    </div>
  </div>
</template>

<script>
import { i18n } from '../i18n.js'
import config from '../config'
import AboutService from '../components/AboutService.vue'

export default {
  components: { AboutService },
  i18n,
  props: {
    authorization: {
      type: String
    },
    locale: {
      type: String,
      default: ''
    },
    origin: {
      type: String,
      default: ''
    },
    sandbox: {
      type: Boolean,
      default: false
    },
    canCancel: {
      type: Boolean,
      default: false
    },
    noRedirect: {
      type: Boolean,
      default: false
    },
    externalConfirm: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      apiOrigin: null,
      appName: '',
      appDescription: '',
      accessScope: 'your payment accounts data',
      error: '',
      starting: true,
      confirming: false,
      confirmed: false
    }
  },
  computed: {
    isEnablebankingOrigin () {
      return config.enablebankingOrigins.includes(this.apiOrigin) // check if apiOrigin is a default origin
    },
    termsUrl () {
      return this.apiOrigin + '/terms' + (
        this.locale ? `?locale=${this.locale.toUpperCase()}` : ''
      )
    },
    appDescriptionLocal: function () {
      if (typeof this.appDescription === 'string' && this.appDescription.length > 0) {
        return this.appDescription
      } else if (
        this.appDescription &&
        typeof this.appDescription[this.$i18n.locale] === 'string' &&
        this.appDescription[this.$i18n.locale].length > 0
      ) {
        return this.appDescription[this.$i18n.locale]
      } else {
        return this.$t('Application description is not provided.')
      }
    }
  },
  async mounted () {
    if (this.locale) {
      this.$i18n.locale = this.locale.toUpperCase()
    }
    this.apiOrigin = this.origin || (this.sandbox ? config.publicSandboxOrigin : config.publicOrigin) // if origin is not provided, use default origin
    try {
      const r = await fetch(
        this.apiOrigin + '/api/start_consent_external',
        {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            authorization_id: this.authorization
          })
        }
      )
      const response = (await r.json()).response
      if (response.broker_public_url) {
        this.apiOrigin = response.broker_public_url // if broker_public_url is provided by sessions endpoint, use it
      }
      this.appName = response.app_name
      this.appDescription = response.app_description
      if (response.access) {
        if (response.access.balances && response.access.transactions) {
          this.accessScope = 'the list of your payment accounts, accounts balances and transactions'
        } else if (response.access.balances) {
          this.accessScope = 'the list of your payment accounts and accounts balances'
        } else if (response.access.transactions) {
          this.accessScope = 'the list of your payment accounts and accounts transactions'
        } else {
          this.accessScope = 'the list of your payment accounts'
        }
      }
      if (this.externalConfirm) {
        const comp = this
        this.$el.parentNode.addEventListener('confirm', function () { comp.submitConsent() })
      }
      this.$emit('ready')
    } catch (e) {
      this.setError(this.$t('Error while updating authentication status'))
    } finally {
      this.starting = false
    }
  },
  methods: {
    async submitConsent () {
      this.confirming = true
      try {
        await fetch(
          this.apiOrigin + '/api/confirm_consent_external',
          {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              authorization_id: this.authorization
            })
          }
        )
        this.confirmed = true
        this.$emit('confirmed')
        if (!this.noRedirect) {
          let redirectUrl = this.apiOrigin + `/ais/start?sessionid=${this.authorization}`
          if (this.locale) {
            redirectUrl += `&locale=${this.locale.toUpperCase()}`
          }
          window.location.href = redirectUrl
        }
      } catch (e) {
        this.setError(this.$t('Unable to continue authorization'))
      } finally {
        this.confirming = false
      }
    },
    async cancelAuth () {
      this.$emit('cancelled')
    },
    setError (msg) {
      this.error = msg
      this.$emit('error', msg)
    }
  }
}
</script>
